<!--
  DAVMAN AI INTEGRATION
  Este archivo es parte de la integración personalizada de IA para Davman Persa.
  NO es parte del código original de Chatwoot.

  Pestaña de estadísticas del asistente IA
-->
<script setup>
import { ref, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import { getStats, resetStats } from 'dashboard/api/davmanAi';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  dashboardData: {
    type: Object,
    default: null,
  },
});

const loading = ref(false);
const resetting = ref(false);
const stats = ref(null);
const showResetConfirm = ref(false);

const uptimeFormatted = computed(() => {
  if (!stats.value?.uptimeHours) return '0h';
  const hours = stats.value.uptimeHours;
  if (hours < 24) return `${hours.toFixed(1)}h`;
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return `${days}d ${remainingHours.toFixed(0)}h`;
});

const usageRate = computed(() => {
  if (!stats.value) return 0;
  const { suggestionsGenerated, suggestionsUsed } = stats.value;
  if (!suggestionsGenerated) return 0;
  return Math.round((suggestionsUsed / suggestionsGenerated) * 100);
});

const topIntents = computed(() => {
  if (!stats.value?.intents) return [];
  const intents = Object.entries(stats.value.intents);
  return intents.sort((a, b) => b[1] - a[1]).slice(0, 5);
});

const intentLabels = {
  consulta_producto: 'Consulta de producto',
  precio: 'Precio',
  disponibilidad: 'Disponibilidad',
  horarios: 'Horarios',
  envios: 'Envíos',
  cita: 'Citas',
  queja: 'Queja',
  otro: 'Otro',
};

const getIntentLabel = (intent) => intentLabels[intent] || intent;

const loadStats = async () => {
  loading.value = true;
  try {
    stats.value = await getStats();
  } catch (error) {
    console.error('[Davman AI] Error loading stats:', error);
  } finally {
    loading.value = false;
  }
};

const handleReset = async () => {
  resetting.value = true;
  try {
    await resetStats();
    useAlert('Estadísticas reiniciadas correctamente');
    showResetConfirm.value = false;
    await loadStats();
  } catch (error) {
    useAlert('Error al reiniciar estadísticas');
    console.error('[Davman AI] Error resetting stats:', error);
  } finally {
    resetting.value = false;
  }
};

onMounted(() => {
  loadStats();
});
</script>

<template>
  <div>
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h3 class="text-lg font-medium text-n-slate-12">
          Estadísticas de Uso
        </h3>
        <p class="text-sm text-n-slate-11">
          Métricas de rendimiento del asistente de IA.
        </p>
      </div>
      <div class="flex gap-2">
        <Button
          icon="i-lucide-refresh-cw"
          label="Actualizar"
          color="slate"
          faded
          :is-loading="loading"
          @click="loadStats"
        />
        <Button
          icon="i-lucide-trash-2"
          label="Reiniciar"
          color="ruby"
          faded
          @click="showResetConfirm = true"
        />
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading && !stats" class="flex items-center justify-center py-12">
      <span class="i-lucide-loader-2 size-8 animate-spin text-n-slate-11" />
    </div>

    <div v-else-if="stats" class="flex flex-col gap-8">
      <!-- Stats Grid -->
      <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <div class="p-4 rounded-xl bg-n-solid-3">
          <div class="flex items-center gap-2 mb-2">
            <span class="i-lucide-message-circle size-5 text-n-slate-11" />
            <span class="text-sm text-n-slate-11">Mensajes procesados</span>
          </div>
          <div class="text-3xl font-semibold text-n-slate-12">
            {{ stats.messagesProcessed || 0 }}
          </div>
        </div>

        <div class="p-4 rounded-xl bg-n-solid-3">
          <div class="flex items-center gap-2 mb-2">
            <span class="i-lucide-sparkles size-5 text-n-slate-11" />
            <span class="text-sm text-n-slate-11">Sugerencias generadas</span>
          </div>
          <div class="text-3xl font-semibold text-n-slate-12">
            {{ stats.suggestionsGenerated || 0 }}
          </div>
        </div>

        <div class="p-4 rounded-xl bg-n-solid-3">
          <div class="flex items-center gap-2 mb-2">
            <span class="i-lucide-check-circle size-5 text-green-500" />
            <span class="text-sm text-n-slate-11">Sugerencias usadas</span>
          </div>
          <div class="flex items-baseline gap-2">
            <span class="text-3xl font-semibold text-n-slate-12">
              {{ stats.suggestionsUsed || 0 }}
            </span>
            <span class="text-sm text-green-500">
              ({{ usageRate }}%)
            </span>
          </div>
        </div>

        <div class="p-4 rounded-xl bg-n-solid-3">
          <div class="flex items-center gap-2 mb-2">
            <span class="i-lucide-x-circle size-5 text-red-500" />
            <span class="text-sm text-n-slate-11">Sugerencias descartadas</span>
          </div>
          <div class="text-3xl font-semibold text-n-slate-12">
            {{ stats.suggestionsDismissed || 0 }}
          </div>
        </div>
      </div>

      <!-- Secondary Stats -->
      <div class="grid gap-4 md:grid-cols-3">
        <div class="p-4 rounded-xl border border-n-weak">
          <div class="flex items-center gap-2 mb-2">
            <span class="i-lucide-book-open size-5 text-n-slate-11" />
            <span class="text-sm text-n-slate-11">Matches de FAQ</span>
          </div>
          <div class="text-2xl font-semibold text-n-slate-12">
            {{ stats.faqMatches || 0 }}
          </div>
        </div>

        <div class="p-4 rounded-xl border border-n-weak">
          <div class="flex items-center gap-2 mb-2">
            <span class="i-lucide-brain size-5 text-n-slate-11" />
            <span class="text-sm text-n-slate-11">Llamadas a LLM</span>
          </div>
          <div class="text-2xl font-semibold text-n-slate-12">
            {{ stats.llmCalls || 0 }}
          </div>
        </div>

        <div class="p-4 rounded-xl border border-n-weak">
          <div class="flex items-center gap-2 mb-2">
            <span class="i-lucide-clock size-5 text-n-slate-11" />
            <span class="text-sm text-n-slate-11">Tiempo activo</span>
          </div>
          <div class="text-2xl font-semibold text-n-slate-12">
            {{ uptimeFormatted }}
          </div>
        </div>
      </div>

      <!-- Intent Distribution -->
      <div v-if="topIntents.length" class="p-4 rounded-xl border border-n-weak">
        <h4 class="text-sm font-medium text-n-slate-12 mb-4">
          Distribución de Intenciones
        </h4>
        <div class="flex flex-col gap-3">
          <div
            v-for="[intent, count] in topIntents"
            :key="intent"
            class="flex items-center gap-4"
          >
            <span class="w-32 text-sm text-n-slate-11 truncate">
              {{ getIntentLabel(intent) }}
            </span>
            <div class="flex-1 h-4 rounded-full bg-n-solid-3 overflow-hidden">
              <div
                class="h-full rounded-full"
                style="background-color: #87785B"
                :style="{
                  width: `${(count / topIntents[0][1]) * 100}%`,
                }"
              />
            </div>
            <span class="w-12 text-sm text-right text-n-slate-12">
              {{ count }}
            </span>
          </div>
        </div>
      </div>

      <!-- No intents -->
      <div
        v-else
        class="flex flex-col items-center justify-center py-8 text-center rounded-xl border border-n-weak"
      >
        <span class="i-lucide-chart-bar size-12 text-n-slate-9 mb-4" />
        <p class="text-sm text-n-slate-11">
          Aún no hay datos de intenciones registrados.
        </p>
      </div>
    </div>

    <!-- Reset Confirmation Modal -->
    <woot-delete-modal
      v-model:show="showResetConfirm"
      :on-close="() => (showResetConfirm = false)"
      :on-confirm="handleReset"
      title="Reiniciar Estadísticas"
      message="¿Estás seguro de que deseas reiniciar todas las estadísticas? Esta acción no se puede deshacer."
      confirm-text="Sí, reiniciar"
      reject-text="No, cancelar"
    />
  </div>
</template>
